import {
  EXTERNAL_LINKS,
  getCreativeBuilderUrl,
  Paths,
} from '@src/config/paths';
import { Icon } from '@components/Icon.tsx';
import { IconType } from '@src/IconType.ts';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MenuItem } from '@components/Menu/MenuItem.tsx';
import { MenuItemType } from '@src/config/constants';
import { MenuSeperator } from '@components/Menu/MenuSeperator.tsx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@src/contexts/useUserContext.ts';
import authenticationService from '@src/api/services/AuthenticationService.ts';
import { useIsSmallScreen } from '@src/hooks/useMediaQuery';

const PersonalMenuContent = () => {
  const { t } = useTranslation('menu');
  const { profilePictureUrl, language, isAdminUser, isAgencyUser } =
    useUserContext();
  const navigate = useNavigate();
  const isPhone = useIsSmallScreen();
  const logout = useCallback(async () => {
    await authenticationService.logout();
    navigate('/login');
  }, [navigate]);

  return (
    <>
      <MenuItem
        type={MenuItemType.LINK}
        href="/jobs"
        icon={IconType.Briefcase}
        label={t('jobs')}
        showLabel={isPhone}
        className="flex"
        labelClass="text-xs"
      />
      <MenuItem
        type={MenuItemType.LINK}
        href="/organizations"
        icon={IconType.Building}
        label={t('organizations')}
        showLabel={isPhone}
        className="flex"
        labelClass="text-xs"
      />
      <MenuItem
        type={MenuItemType.LINK}
        href={Paths.COACHING}
        icon={IconType.YoutubeLogo}
        label={t('coaching')}
        showLabel={isPhone}
        className="flex"
        labelClass="text-xs"
      />
      {(isAdminUser || isAgencyUser) && (
        <MenuItem
          type={MenuItemType.LINK}
          href={getCreativeBuilderUrl()}
          icon={IconType.PaintBrushBroad}
          label={t('creativeBuilder')}
          className="hidden sm:flex"
          labelClass="text-xs"
        />
      )}
      <div className="hidden sm:flex flex-grow"></div>

      <MenuItem
        type={MenuItemType.DROPDOWN}
        icon={isPhone ? IconType.DotsThreeOutline : IconType.UserCircle}
        picture={!isPhone && profilePictureUrl ? profilePictureUrl : undefined}
        label={isPhone ? t('more') : undefined}
        showLabel={isPhone}
        className="flex"
        labelClass="text-xs"
        dropDownMenuEntries={[
          <MenuItem
            type={MenuItemType.LINK}
            icon={IconType.UserCircle}
            picture={profilePictureUrl ?? undefined}
            label={t('profile')}
            href="/profile"
            showLabel={true}
            key="profile"
            isSubMenuItem={true}
          />,
          <MenuSeperator key="seperator-0" />,
          <MenuItem
            type={MenuItemType.LINK}
            icon={IconType.Support24}
            label={t('support')}
            showLabel={true}
            href={Paths.SUPPORT}
            key="support"
            isSubMenuItem={true}
          />,
          <MenuItem
            type={MenuItemType.LINK}
            icon={IconType.ChatTeardropText}
            label={t('imprint')}
            showLabel={true}
            href={EXTERNAL_LINKS.IMPRINT[language]}
            target={'_blank'}
            key="imprint"
            isSubMenuItem={true}
          />,
          <MenuItem
            type={MenuItemType.LINK}
            icon={IconType.Key}
            label={t('privacyPolicy')}
            showLabel={true}
            href={EXTERNAL_LINKS.PRIVACY_POLICY[language]}
            target={'_blank'}
            key="privacyPolicy"
            isSubMenuItem={true}
          />,
          <MenuItem
            type={MenuItemType.LINK}
            icon={IconType.Book}
            label={t('termsAndConditions')}
            showLabel={true}
            href={EXTERNAL_LINKS.TERMS_AND_CONDITIONS[language]}
            target={'_blank'}
            key="termsAndConditions"
            isSubMenuItem={true}
          />,
          <MenuSeperator key="seperator-1" />,
          <MenuItem
            type={MenuItemType.BUTTON}
            icon={IconType.Logout}
            label={t('logout')}
            onClick={() => void logout()}
            showLabel={true}
            key="logout"
            isSubMenuItem={true}
          />,
        ]}
      />
    </>
  );
};

const LogoContent = () => {
  return (
    <div className="hidden sm:flex px-6 py-4 text-slate-900">
      <div className="flex w-12 h-[47px] py-2.5 px-4">
        <Link to={Paths.JOBS} replace={true}>
          <Icon
            type={IconType.LogoSmall}
            className="w-[17px] h-[27px]"
            aria-hidden="true"
          />
        </Link>
      </div>
    </div>
  );
};

const MenuContent = () => {
  return (
    <nav className="space-y-1 flex-grow flex flex-row sm:flex-col gap-0 sm:gap-4 px-4 sm:px-6 pt-4 sm:pt-6 pb-4 items-start justify-between sm:justify-start">
      <PersonalMenuContent />
    </nav>
  );
};

export default function Menu() {
  const location = useLocation();
  const [isDetailsView, setIsDetailsView] = useState(false);
  const isPhone = useIsSmallScreen();

  useEffect(() => {
    const moreThan2Segments =
      location.pathname.split('/').filter(Boolean).length > 1;
    setIsDetailsView(moreThan2Segments);
  }, [location.pathname]);

  if (isPhone && isDetailsView) {
    return null;
  }

  return (
    <div className="flex sm:max-h-full sm:min-h-full flex-shrink-0 bg-white sm:has-[.dropdown-menu-open]:w-[400px] sm:has-[.dropdown-menu-open]:-mr-[300px] sm:has-[.menuitem>.rounded-xl:hover]:hover:-mr-[300px] sm:has-[.menuitem>.rounded-xl:hover]:hover:w-[400px]">
      <div className="sticky bottom-0 flex flex-col justify-between overflow-y-auto scrollbar-hide h-full w-full max-h-screen z-[var(--z-index-sidebar)]">
        <LogoContent />
        <MenuContent />
      </div>
    </div>
  );
}
